/**
 * Advanced answer checking with numeric equivalence, unit stripping, and
 * multiple answers. Lithuanian letters are compared as written — „a" does not
 * match „ą" — so a word typed without its marks is graded as misspelt. Mirrors
 * app/services/answer_checker.rb.
 */

export type CheckResult = "CORRECT" | "INCORRECT";

/**
 * A question's exceptions to how a typed answer is checked, from its own
 * `answer_rules` — mirrors AnswerChecker::Rules. Absent means the defaults:
 * case forgiven, Lithuanian marks compared as written.
 */
export type AnswerRules = {
  /** „Vilnius" ≠ „vilnius" — for capital-letter questions. */
  caseSensitive?: boolean;
  /** „nesti" = „nešti" — only where the marks are not what is being taught. */
  ignoreDiacritics?: boolean;
  /**
   * The answer must be written as the key or one of its alternatives, never
   * merely equal in value — for questions whose skill IS the form („Suprastink
   * 6/8" must not accept „6/8" or „0,75"). Spacing, KaTeX, the decimal comma or
   * point, the multiplication sign, thousands spaces, superscript powers and a
   * space before „%" are still forgiven. Every equivalence of value is off:
   * unit conversion, an inequality for its interval, and reordered terms of a
   * sum („18-11y" for „-11y+18") — so a question teaching term order sets it.
   */
  exactForm?: boolean;
  /**
   * The kind of number the answer must be WRITTEN as — "mixed", "fraction",
   * "improper", "decimal", "percent" — and equal the key in value. For
   * „convert this" questions: „Paversk 8/3 mišriuoju skaičiumi" refuses "8/3"
   * copied back from the question while still taking "4 3/5" where the key is
   * "4 6/10". A bare number under "percent" reads as that many percent.
   */
  form?: string | null;
};

/**
 * The answer as the form it must be written in, or null when it is written some
 * other way — mirrors `AnswerChecker.in_form`. A trailing unit is dropped, and a
 * percent answer gains the "%" a child may leave off.
 */
function inForm(answer: string, form: string): string | null {
  let s = stripKatex(answer).replace(/\s+/g, " ").trim();
  // A one-letter label in front and a unit behind — mirrors AnswerChecker.in_form.
  s = s.replace(/^\p{L}[₀-₉\d]?\s*=\s*/u, "");
  if (!(form === "percent" && s.endsWith("%"))) s = s.replace(/\s*(?:°\s*[CF]?|[\p{L}€$][\p{L}²³\d.]*)$/u, "");
  s = s.replace(/\s*%$/, "%");
  let ok = false;
  let m: RegExpMatchArray | null;
  switch (form) {
    case "decimal": ok = /^-?\d+(?:[.,]\d+)?$/.test(s); break;
    case "percent": ok = /^-?\d+(?:[.,]\d+)?%?$/.test(s); break;
    case "fraction": ok = /^-?\d+\/\d+$/.test(s); break;
    case "improper":
      m = s.match(/^-?(\d+)\/(\d+)$/);
      ok = !!m && Number(m[1]) >= Number(m[2]);
      break;
    case "mixed":
      m = s.match(/^-?\d+ (\d+)\/(\d+)$/);
      ok = /^-?\d+$/.test(s) || (!!m && Number(m[1]) < Number(m[2]));
      break;
  }
  if (!ok) return null;
  return form === "percent" && !s.endsWith("%") ? `${s}%` : s;
}

const DIACRITIC_FOLD: Record<string, string> = {
  ą: "a", č: "c", ę: "e", ė: "e", į: "i", š: "s", ų: "u", ū: "u", ž: "z",
  Ą: "A", Č: "C", Ę: "E", Ė: "E", Į: "I", Š: "S", Ų: "U", Ū: "U", Ž: "Z",
};
export type MultiCheckResult = "CORRECT" | "INCORRECT" | "PARTIAL";

const COMMON_UNITS = [
  "cm²", "cm2", "m²", "m2", "km²", "km2", "dm²", "dm2", "mm²", "mm2",
  "cm³", "cm3", "m³", "m3", "dm³", "dm3", "mm³", "mm3",
  "cm", "mm", "dm", "km", "m",
  "kg", "mg", "g", "cnt", "t",
  "ml", "dl", "cl", "l",
  "min", "val", "sek", "s", "h",
  "€", "eur", "ct",
  "°", "°c",
];

/**
 * Strip KaTeX/LaTeX notation and convert to plain text.
 * - $\frac{1}{3}$ -> 1/3
 * - $2\frac{1}{4}$ -> 2 1/4
 * - $-\frac{3}{4}$ -> -3/4
 * - $\sqrt{16}$ -> sqrt(16)
 * - $x^2$ -> x^2
 * - Plain text passes through unchanged.
 */
const KATEX_SYMBOLS: [string, string][] = [
  ["geq", "≥"], ["ge", "≥"], ["leq", "≤"], ["le", "≤"], ["neq", "≠"], ["ne", "≠"],
  ["infty", "∞"], ["cup", "∪"], ["cap", "∩"], ["emptyset", "∅"], ["varnothing", "∅"],
  ["pi", "π"],
];

export function stripKatex(s: string): string {
  let result = s;
  // Remove $ delimiters (both inline $...$ and display $$...$$)
  result = result.replace(/\$\$/g, "").replace(/\$/g, "");
  // Convert mixed numbers: 2\frac{1}{4} -> 2 1/4
  result = result.replace(/(-?\d+)\\frac\{(\d+)\}\{(\d+)\}/g, "$1 $2/$3");
  // Convert fractions: \frac{1}{3} -> 1/3
  result = result.replace(/\\frac\{(-?\d+)\}\{(\d+)\}/g, "$1/$2");
  // Convert sqrt: \sqrt{16} -> sqrt(16)
  result = result.replace(/\\sqrt\{([^}]+)\}/g, "sqrt($1)");
  // A degree sign: $180^\circ$ and $180^{\circ}$ -> 180°
  result = result.replace(/\^\s*(?:\{\s*\\circ\s*\}|\\circ(?![a-z]))/gi, "°");
  result = result.replace(/\\%/g, "%");
  // Convert powers: x^{10} -> x^10, already plain x^2 stays
  result = result.replace(/\^\{([^}]+)\}/g, "^$1");
  // Unwrap text and the decimal comma: \text{ cm} -> " cm", 0{,}5 -> 0,5
  result = result.replace(/\\text\{([^}]*)\}/g, "$1");
  // Any other fraction is its numerator over its denominator, each kept whole:
  // \frac{y+3}{x+2} -> (y+3)/(x+2). Innermost first.
  const GENERIC_FRAC_RE = /\\[dt]?frac\{([^{}]*)\}\{([^{}]*)\}/;
  while (GENERIC_FRAC_RE.test(result)) result = result.replace(new RegExp(GENERIC_FRAC_RE.source, "g"), "($1)/($2)");
  result = result.replace(/\{,\}/g, ",");
  // Remove remaining backslash commands (e.g. \cdot, \times, \pm)
  result = result.replace(/\\(cdot|times)/g, "*");
  result = result.replace(/\\pm/g, "±");
  // Symbol commands, and the ASCII spellings a child types for them:
  // $a \ge b$ is "a ≥ b" is "a >= b".
  for (const [cmd, sym] of KATEX_SYMBOLS) result = result.replace(new RegExp(`\\\\${cmd}(?![a-zA-Z])`, "g"), sym);
  result = result.replace(/>=/g, "≥").replace(/<=/g, "≤").replace(/!=/g, "≠");
  result = result.replace(/\\\s/g, " ");
  // Clean up extra spaces
  result = result.replace(/\s+/g, " ").trim();
  return result;
}

/** Whitespace only. Case and marks are decided once, by `prepare`, at the
 *  entry points — everything below compares strings already prepared. */
function normalize(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

/** A dash is typed as a hyphen: between digits and as a spaced dash between
 *  words, „–", „−" and „—" are „-". Mirrors `prepare` in answer_checker.rb. */
const DIGIT_DASH_RE = /(?<=\d)\s*[−–—]\s*(?=\d)|(?<![\p{L}\d])[−–—](?=\d)/gu;
const SPACED_DASH_RE = /\s+[−–—]\s+/g;
/** A keyboard types "…"; „…“ is the same quotation. */
const QUOTE_MARKS_RE = /[„“”«»]/g;

function prepare(s: string, rules?: AnswerRules | null): string {
  // Composed first: a key stored as „z" + combining caron is not the „ž" a child types.
  let value = s.normalize("NFC").replace(DIGIT_DASH_RE, "-").replace(SPACED_DASH_RE, " - ").replace(QUOTE_MARKS_RE, '"');
  if (!rules?.caseSensitive) value = value.toLowerCase();
  if (rules?.ignoreDiacritics) value = value.split("").map((c) => DIACRITIC_FOLD[c] || c).join("");
  return normalize(value);
}

function stripUnits(s: string): string {
  let result = s.trim();
  // Sort units by length (longest first) to avoid partial matches
  const sorted = [...COMMON_UNITS].sort((a, b) => b.length - a.length);
  for (const unit of sorted) {
    // Strip unit from the end of the string
    // Never the tail of a word: „pūkuotas" is not „pūkuota" plus seconds.
    const boundary = /^\p{L}/u.test(unit) ? "(?<!\\p{L})" : "";
    const pattern = new RegExp(`${boundary}\\s*${escapeRegex(unit)}\\s*$`, "iu");
    result = result.replace(pattern, "").trim();
  }
  return result;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * The value of an answer that is ONE number: an integer, a decimal (comma or
 * point), a fraction or a mixed number, optionally followed by a unit or by
 * plain words („5 obuoliai"). Null for anything holding a second number —
 * „3 ir 6", „10 cm 5 mm", „5–6", „12:45" — which used to be read as its leading
 * number and so matched a key of „3", „10", „5" or „12". Null too for a letter
 * stuck to the number that is not a unit: „2x" is a term, and it was graded
 * equal to „2y". Mirrors AnswerChecker.parse_numeric.
 */
const NUMBER_RE =
  /^(?:(-?\d+)\s+(\d+)\s*\/\s*(\d+)|(-?\d+)\s*\/\s*(\d+)|([+-]?(?:\d+(?:[.,]\d*)?|[.,]\d+)(?:[eE][+-]?\d+)?))/;
const DIGIT_RE = /[\d²³]/;
const EXPRESSION_TAIL_RE = /^[+\-−–×·*/:=<>^√π]|[+=<>^×·*√π]/;
/** Except a Lithuanian ordinal ending on the number: „2-as", „1-ojo asmens". */
const ORDINAL_SUFFIX_RE = /^-\p{L}+(?:\s+\p{L}+)*$/u;

function parseNumeric(s: string): number | null {
  const cleaned = collapseThousands(s.trim());
  const m = cleaned.match(NUMBER_RE);
  if (!m) return null;

  const rest = cleaned.slice(m[0].length);
  const unitless = stripUnits(rest);
  if (DIGIT_RE.test(unitless)) return null;
  if (/^\p{L}/u.test(rest) && unitless) return null;
  // What follows is an expression when it holds an operator: „1 - a + b" is
  // not „1". Mirrors AnswerChecker::EXPRESSION_TAIL_RE.
  if (EXPRESSION_TAIL_RE.test(unitless) && !ORDINAL_SUFFIX_RE.test(rest)) return null;

  if (m[1] !== undefined) {
    const den = parseInt(m[3], 10);
    if (den === 0) return null;
    // The sign belongs to the whole number: „-2 1/4" is -(2 + 1/4).
    const whole = Math.abs(parseInt(m[1], 10)) + parseInt(m[2], 10) / den;
    return m[1].startsWith("-") ? -whole : whole;
  }
  if (m[4] !== undefined) {
    const den = parseInt(m[5], 10);
    if (den === 0) return null;
    return parseInt(m[4], 10) / den;
  }
  const value = Number(m[6].replace(",", ".").replace(/\.$/, ""));
  return Number.isFinite(value) ? value : null;
}

/**
 * Clock times are compared as hours and minutes, never as numbers: „8:30" is
 * not „8" and „10.55" is „10:55". The key decides whether this applies — it is
 * a time when it reads H:MM, or H.MM with two digits of minutes under 60 (a
 * Lithuanian decimal takes a comma, so „3.50" is far likelier a time than
 * money). Against such a key the answer may be H:MM, H.MM or a bare hour („8",
 * „8 val."). The one reverse case is a bare-hour key answered with H:MM („8:00"
 * for „8"), which would otherwise be read as two numbers.
 *
 * Hours are equal modulo 12 („13:00" = „1:00"): these are clock-reading
 * questions, and an analogue face cannot tell morning from afternoon. Mirrors
 * AnswerChecker.same_time?.
 */
// „3 valandos", „10 valandų", „3 valandą" — mirrors AnswerChecker::TIME_SUFFIX.
const TIME_SUFFIX = String.raw`(?:\s*(?:val\.?|h|valand(?:a|ą|os|ų|as)))?`;
// „trečia", „trečią valandą" — mirrors AnswerChecker::ORDINAL_HOUR_RE.
const HOUR_ORDINALS = ["pirm", "antr", "treči", "ketvirt", "penkt", "šešt", "septint", "aštunt", "devint", "dešimt", "vienuolikt", "dvylikt"];
const ORDINAL_HOUR_RE = new RegExp(String.raw`^(${HOUR_ORDINALS.join("|")})(?:a|ą)(?:\s+valand(?:a|ą))?$`, "i");
// „šešios valandos", „dvi valandas", „dvylika" — mirrors AnswerChecker::SPOKEN_HOURS.
const SPOKEN_HOURS: Record<string, number> = {
  viena: 1, vienas: 1, "vieną": 1, dvi: 2, du: 2,
  trys: 3, tris: 3, keturios: 4, keturias: 4, keturi: 4,
  penkios: 5, penkias: 5, penki: 5, "šešios": 6, "šešias": 6,
  "šeši": 6, septynios: 7, septynias: 7, septyni: 7,
  "aštuonios": 8, "aštuonias": 8, "aštuoni": 8, devynios: 9,
  devynias: 9, devyni: 9, "dešimt": 10, vienuolika: 11, dvylika: 12,
};
const SPOKEN_HOUR_RE = new RegExp(
  String.raw`^(${Object.keys(SPOKEN_HOURS).sort((a, b) => b.length - a.length).join("|")})(?:\s+valand(?:a|ą|os|ų|as))?$`,
  "i",
);
const COLON_TIME_RE = new RegExp(String.raw`^(\d{1,2})\s*:\s*(\d{2})${TIME_SUFFIX}$`, "i");
const DOT_TIME_RE = new RegExp(String.raw`^(\d{1,2})\.(\d{2})${TIME_SUFFIX}$`, "i");
const BARE_HOUR_RE = new RegExp(String.raw`^(\d{1,2})${TIME_SUFFIX}$`, "i");
/** The school spelling of a clock time, „16 val. 25 min." — accepted as an
 *  answer against an H:MM key. */
const VAL_MIN_TIME_RE = /^(\d{1,2})\s*(?:val|h)\.?\s*(\d{1,2})\s*min\.?$/i;

/**
 * A duration is compared by its total length — mirrors AnswerChecker
 * DURATION_UNITS: „1 val. 55 min." is „115 min", „1 val 55 min" and „1 h 55
 * min"; „1 val. 5 min." is „65 min". Each part is a number and one of these
 * units, a trailing dot ignored. The folded spellings are there for questions
 * whose `ignoreDiacritics` folds them.
 */
const DURATION_UNITS: Record<string, number> = {
  valandų: 3600, valandos: 3600, valanda: 3600, valandu: 3600, val: 3600, h: 3600,
  minučių: 60, minutės: 60, minutė: 60, minuciu: 60, minutes: 60, minute: 60, min: 60,
  sekundžių: 1, sekundės: 1, sekundė: 1, sekundziu: 1, sekundes: 1, sekunde: 1, sek: 1, s: 1,
};
const DURATION_PART = String.raw`(\d+(?:[.,]\d+)?)\s*(${Object.keys(DURATION_UNITS)
  .sort((a, b) => b.length - a.length)
  .join("|")})\.?(?![\p{L}])`;
const DURATION_PART_RE = new RegExp(DURATION_PART, "giu");
const DURATION_RE = new RegExp(String.raw`^(?:${DURATION_PART}\s*)+$`, "iu");

/**
 * The unit a single number carries, so „45 valandos" is not graded equal to
 * „45 min." and „45 m" not to „45 cm" — mirrors AnswerChecker::UNIT_ALIASES.
 * Spellings of one unit share a name. A side with no unit, or with a plain
 * word („5 obuoliai"), carries none and conflicts with nothing.
 */
const UNIT_ALIASES: Record<string, string> = {
  cm2: "cm²", m2: "m²", km2: "km²", dm2: "dm²", mm2: "mm²",
  cm3: "cm³", m3: "m³", dm3: "dm³", mm3: "mm³",
  eur: "€", "°c": "°", val: "h", sek: "s",
  ...Object.fromEntries(
    Object.entries(DURATION_UNITS).map(([name, seconds]) => [name, seconds === 3600 ? "h" : seconds === 60 ? "min" : "s"])
  ),
};

/** The canonical unit of an answer that is one number followed by a unit;
 *  null when it carries none, or is not a single number at all. */
function unitOf(s: string): string | null {
  const cleaned = collapseThousands(s.trim());
  const m = cleaned.match(NUMBER_RE);
  if (!m || parseNumeric(cleaned) === null) return null;

  const word = cleaned.slice(m[0].length).trim().toLowerCase().replace(/\.$/, "").trim();
  if (!word) return null;

  const unit = UNIT_ALIASES[word] ?? word;
  return COMMON_UNITS.includes(unit) || unit === "%" ? unit : null;
}

/**
 * Units of one dimension are converted before they are compared, so „8 cm" is
 * „80 mm" and „600 g" is „0,6 kg"; two dimensions never match — mirrors
 * AnswerChecker::UNIT_SCALE. Each unit's size in the dimension's smallest
 * common measure.
 */
const UNIT_SCALE: Record<string, [string, number]> = {
  mm: ["length", 1], cm: ["length", 10], dm: ["length", 100], m: ["length", 1000], km: ["length", 1_000_000],
  "mm²": ["area", 1], "cm²": ["area", 100], "dm²": ["area", 10_000], "m²": ["area", 1_000_000],
  "km²": ["area", 1_000_000_000_000],
  mg: ["mass", 1], g: ["mass", 1000], kg: ["mass", 1_000_000], cnt: ["mass", 100_000_000], t: ["mass", 1_000_000_000],
  "mm³": ["volume", 1], ml: ["volume", 1000], "cm³": ["volume", 1000], cl: ["volume", 10_000],
  dl: ["volume", 100_000], l: ["volume", 1_000_000], "dm³": ["volume", 1_000_000], "m³": ["volume", 1_000_000_000],
  s: ["time", 1], min: ["time", 60], h: ["time", 3600],
  ct: ["money", 1], "€": ["money", 100],
};

/**
 * True or false when both sides are one number in two different units —
 * converted when they measure the same thing, false when they do not — and
 * null otherwise, which leaves the comparison to the plain number. Mirrors
 * AnswerChecker.same_quantity.
 */
function sameQuantity(userPlain: string, expectedPlain: string): boolean | null {
  const userUnit = unitOf(userPlain);
  const expectedUnit = unitOf(expectedPlain);
  if (userUnit === null || expectedUnit === null || userUnit === expectedUnit) return null;

  const user = UNIT_SCALE[userUnit];
  const expected = UNIT_SCALE[expectedUnit];
  if (!user || !expected || user[0] !== expected[0]) return false;

  const a = (parseNumeric(userPlain) as number) * user[1];
  const b = (parseNumeric(expectedPlain) as number) * expected[1];
  return Math.abs(a - b) < 0.0001 * Math.max(Math.abs(a), Math.abs(b), 1);
}

/** Total seconds of a duration („1 val. 55 min." → 6900); null for anything
 *  that is not wholly made of number-and-time-unit parts. */
function durationSeconds(s: string): number | null {
  if (!DURATION_RE.test(s)) return null;
  let total = 0;
  for (const m of s.matchAll(DURATION_PART_RE)) {
    total += Number(m[1].replace(",", ".")) * DURATION_UNITS[m[2].toLowerCase()];
  }
  return total;
}

/** True or false when both sides are durations, null when either is not — a
 *  bare „115" says nothing about which unit it is in. */
function sameDuration(userPlain: string, expectedPlain: string): boolean | null {
  const user = durationSeconds(userPlain);
  const expected = durationSeconds(expectedPlain);
  if (user === null || expected === null) return null;
  return Math.abs(user - expected) < 0.0001;
}

function clockTime(s: string, ...patterns: RegExp[]): [number, number] | null {
  for (const re of patterns) {
    const m = s.match(re);
    if (!m) continue;
    if (re === ORDINAL_HOUR_RE) return [(HOUR_ORDINALS.indexOf(m[1].toLowerCase()) + 1) % 12, 0];
    if (re === SPOKEN_HOUR_RE) return [SPOKEN_HOURS[m[1].toLowerCase()] % 12, 0];
    const hours = parseInt(m[1], 10);
    const minutes = m[2] === undefined ? 0 : parseInt(m[2], 10);
    if (hours <= 24 && minutes < 60) return [hours % 12, minutes];
  }
  return null;
}

function sameTime(userPlain: string, expectedPlain: string): boolean {
  let expected = clockTime(expectedPlain, COLON_TIME_RE, DOT_TIME_RE);
  let user: [number, number] | null = null;
  if (expected) {
    user = clockTime(userPlain, COLON_TIME_RE, DOT_TIME_RE, BARE_HOUR_RE, VAL_MIN_TIME_RE, ORDINAL_HOUR_RE, SPOKEN_HOUR_RE);
  } else if ((expected = clockTime(expectedPlain, BARE_HOUR_RE))) {
    user = clockTime(userPlain, COLON_TIME_RE);
  }
  return user !== null && expected !== null && user[0] === expected[0] && user[1] === expected[1];
}

/**
 * Spelling of the same expression — mirrors AnswerChecker.canonical_expression.
 * A multiplication sign between two numbers is one sign however it is typed
 * („6x7", „6 × 7", „6·7", „6*7") — only between digits, so a word holding an
 * „x" is never touched. And the spaces around an operator carry nothing:
 * „600+70+2" is „600 + 70 + 2".
 */
const TIMES_RE = /(\d)\s*[xX×·*]\s*(?=\d)/g;
const OPERATOR_SPACE_RE = /\s*([+\-−–×·*:/=<>≤≥])\s*/g;

/** A space before „%" or „π" is spacing: „10 %" is „10%", „10 π" is „10π".
 *  And „pi" is „π" wherever it stands on its own („π/2" is „pi/2") — never
 *  inside a word („pipirai"). */
const PERCENT_SPACE_RE = /(\d)\s+%/g;
const PI_WORD_RE = /(?<!\p{L})pi(?!\p{L})/giu;
const PI_RE = /(\d)\s+π/g;
/** The imaginary unit after a number or a bracket: „3 i" is „3i". Never a word („3 ir 6"). */
const IMAGINARY_SPACE_RE = /([\d)])\s+i(?!\p{L})/giu;
/** A root sign is one sign however it is spelled: „5 √3" is „5√3", and
 *  „sqrt(3)", „sqrt3" and „√(3)" are „√3" — brackets dropped only around one
 *  number or one letter, never before a further factor. A decimal is never a
 *  root: „8,66" is not „5√3". Mirrors AnswerChecker::ROOT_BRACKETS_RE. */
const ROOT_SPACE_RE = /(\d)\s+√/g;
const SQRT_WORD_RE = /sqrt\s*(?=[(\d])/gi;
const ROOT_BRACKETS_RE = /√\(\s*(\d+(?:[.,]\d+)?|[a-z])\s*\)(?![\p{L}\d(])/giu;
/** A decimal point before π, √ or a letter is the decimal comma: „4.5π" is „4,5π". */
const COEFFICIENT_POINT_RE = /(\d)\.(\d+)(?=[π√a-z])/gi;
/** A function of one letter or one number is one function however it is
 *  spaced or bracketed: „sinB" is „sin B" is „sin(B)", „tan" is „tg", „cot"
 *  is „ctg". Mirrors AnswerChecker::FUNCTION_RE. */
const FUNCTION_RE =
  /(?<!\p{L})(sin|cos|tg|tan|ctg|cot|log|lg|ln)\s*(?:\(\s*(\p{L}|\d+(?:[.,]\d+)?°?)\s*\)|(\p{L}|\d+(?:[.,]\d+)?°?)(?![\p{L}\d]))/giu;
const FUNCTION_NAMES: Record<string, string> = { tan: "tg", cot: "ctg" };

function canonicalExpression(s: string): string {
  return collapseThousands(s)
    .replace(TIMES_RE, "$1×")
    .replace(OPERATOR_SPACE_RE, "$1")
    .replace(PERCENT_SPACE_RE, "$1%")
    .replace(PI_WORD_RE, "π")
    .replace(PI_RE, "$1π")
    .replace(IMAGINARY_SPACE_RE, "$1i")
    .replace(ROOT_SPACE_RE, "$1√")
    .replace(SQRT_WORD_RE, "√")
    .replace(ROOT_BRACKETS_RE, "√$1")
    .replace(COEFFICIENT_POINT_RE, "$1,$2")
    .replace(FUNCTION_RE, (_m, name: string, bracketed?: string, bare?: string) => {
      const n = name.toLowerCase();
      return `${FUNCTION_NAMES[n] ?? n}(${bracketed ?? bare})`;
    });
}

/**
 * Brackets that change nothing („a^(-6)" is „a^-6", „(4t)/(t-5)" is
 * „4t/(t-5)") and a factor written twice („(x+7)(x+7)" is „(x+7)^2") —
 * mirrors AnswerChecker.without_redundant_brackets. A bracketed numerator may
 * drop its brackets when it is one product — a·b/c is (ab)/c however it is
 * read — but a denominator only when it is one number or one letter: „2a/3b"
 * is not safely „2a/(3b)", and brackets around a sum are never dropped. Never
 * under exactForm.
 */
const BRACKETED_POWER_RE = /\^\((-?\d+)\)/g;
const NUMERATOR_BRACKETS_RE = /(?<![\p{L}\d)\]^/:])\((-?(?:\d+(?:[.,]\d+)?)?(?:[a-z](?:\^-?\d+)?)*)\)(?=\/)/giu;
const DENOMINATOR_BRACKETS_RE = /(?<=\/)\(((?:\d+(?:[.,]\d+)?|[a-z])(?:\^-?\d+)?)\)(?![\p{L}\d(^])/giu;
const SQUARED_FACTOR_RE = /(?<!\^)\(([^()]+)\)[·*×]?\(\1\)(?![\^\p{L}\d(])/gu;

function withoutRedundantBrackets(s: string): string {
  return s
    .replace(BRACKETED_POWER_RE, "^$1")
    .replace(NUMERATOR_BRACKETS_RE, (whole: string, inner: string) => (/[\da-z]/i.test(inner) ? inner : whole))
    .replace(DENOMINATOR_BRACKETS_RE, "$1")
    .replace(SQUARED_FACTOR_RE, "($1)^2");
}

/** Every notation-level reading at once: superscript powers, operator
 *  spacing, redundant brackets. */
function tidyExpression(s: string): string {
  return withoutRedundantBrackets(canonicalExpression(superscriptsAsPowers(s)));
}

/**
 * A space between digit groups is a thousands separator — „1 050 000",
 * „300 000 cm", the content's own „1 : 100 000" — when every group after the
 * first has exactly three digits; mirrors AnswerChecker::THOUSANDS_RE. A list
 * key („761, 671, 167") is still read as a list: a list answer is compared item
 * by item, where these spaces separate.
 */
const THOUSANDS_RE = /(?<![\d.,])(\d{1,3})((?:[ \u00A0\u2009\u202F]\d{3})+)(?![\d/])/g;

function collapseThousands(s: string): string {
  return s.replace(THOUSANDS_RE, (_, first: string, rest: string) => first + rest.replace(/\D/g, ""));
}

/**
 * Superscript digits are a power when they follow something that can be raised
 * to one — a letter, a digit or a closing bracket: „a⁶" is „a^6", „9x²" is
 * „9x^2". Compared as an extra reading only, never in place of the written
 * one, so „12 cm²" still reads as a number and a unit wherever units are
 * handled. Mirrors AnswerChecker.superscripts_as_powers.
 */
const SUPERSCRIPT_DIGITS: Record<string, string> = {
  "⁰": "0", "¹": "1", "²": "2", "³": "3", "⁴": "4", "⁵": "5", "⁶": "6", "⁷": "7", "⁸": "8", "⁹": "9", "⁻": "-",
};
const SUPERSCRIPT_RE = /(?<=[\p{L}\d)\]])(⁻?[⁰¹²³⁴⁵⁶⁷⁸⁹]+)/gu;

function superscriptsAsPowers(s: string): string {
  return s.replace(SUPERSCRIPT_RE, (run: string) => "^" + [...run].map((c) => SUPERSCRIPT_DIGITS[c]).join(""));
}

/** One number written as nothing else — no unit, no word; null otherwise. */
function plainNumber(s: string): number | null {
  const v = s.trim();
  const m = v.match(NUMBER_RE);
  if (!m || m[0].length !== v.length) return null;
  return parseNumeric(v);
}

/**
 * The solution of a one-variable inequality, as an inequality or as an
 * interval — mirrors AnswerChecker.same_solution_set?. „x>-14" is „-14<x" is
 * „(-14; +∞)". Strictness is part of the answer — „x>5" is „(5;+∞)", never
 * „[5;+∞)" — and a bare boundary („5") is not the solution of anything.
 */
type Interval = [number, boolean, number, boolean];

const INEQUALITY_OPS = /(<=|>=|≤|≥|⩽|⩾|<|>)/;
const INEQUALITY_OPS_G = /(<=|>=|≤|≥|⩽|⩾|<|>)/g;
const INEQUALITY_OP_NAMES: Record<string, string> = { "<=": "≤", ">=": "≥", "⩽": "≤", "⩾": "≥" };
const LATEX_INEQUALITY_RE = /\\(?:le(?:q(?:slant)?)?|ge(?:q(?:slant)?)?|lt|gt)(?![a-z])/gi;
const LATEX_INEQUALITY: Record<string, string> = { le: "≤", ge: "≥", lt: "<", gt: ">" };
const MIRRORED_OP: Record<string, string> = { "<": ">", ">": "<", "≤": "≥", "≥": "≤" };
const INFINITY_RE = /^([+-]?)\s*(?:∞|\\infty|infinity|infty|inf)$/i;
const ELLIPSIS_RE = /^(?:\.\.\.|…)$/;
const INTERVAL_RE = /^([([])([^()[\]]*)([)\]])$/;
const UNION_RE = /(?<=[)\]])\s*(?:∪|\\cup|u)\s*(?=[([])/i;
const MEMBER_PREFIX_RE = /^[a-z]\s*(?:∈|\\in(?![a-z]))\s*/i;

/** A boundary: a number, or an infinity on the side it belongs to. */
function intervalBound(s: string, side: "lo" | "hi"): number | null {
  const v = s.replace(/[−–]/g, "-").trim();
  const m = v.match(INFINITY_RE);
  if (m) {
    if (side === "lo" ? m[1] !== "-" : m[1] === "-") return null;
    return side === "lo" ? -Infinity : Infinity;
  }
  if (ELLIPSIS_RE.test(v)) return side === "lo" ? -Infinity : Infinity;
  return plainNumber(v);
}

/** One interval, split on „;" — or on the one comma when there is no „;".
 *  Null for a bracket at an infinity, a lower bound not below the upper one
 *  („(20; 10)" is a point), or three parts. Mirrors AnswerChecker.interval. */
function interval(s: string): Interval | null {
  const m = s.trim().match(INTERVAL_RE);
  if (!m) return null;
  const parts = m[2].includes(";") ? m[2].split(";") : m[2].split(",");
  if (parts.length !== 2) return null;

  const lo = intervalBound(parts[0], "lo");
  const hi = intervalBound(parts[1], "hi");
  if (lo === null || hi === null || lo >= hi) return null;

  const loClosed = m[1] === "[";
  const hiClosed = m[3] === "]";
  if ((loClosed && !Number.isFinite(lo)) || (hiClosed && !Number.isFinite(hi))) return null;
  return [lo, loClosed, hi, hiClosed];
}

/** The intervals of „[-4;-1) ∪ (2;5]", in order; null if any part is not one. */
function intervalUnion(s: string): Interval[] | null {
  const v = s.trim().replace(MEMBER_PREFIX_RE, "");
  const intervals = v.split(UNION_RE).map(interval);
  if (intervals.length === 0 || intervals.some((i) => i === null)) return null;
  return (intervals as Interval[]).sort((a, b) => a[0] - b[0] || (a[1] ? 0 : 1) - (b[1] ? 0 : 1));
}

const isVariable = (t: string) => /^[a-z]$/i.test(t);

function ray(op: string, bound: number): Interval | null {
  switch (op) {
    case ">": return [bound, false, Infinity, false];
    case "≥": return [bound, true, Infinity, false];
    case "<": return [-Infinity, false, bound, false];
    case "≤": return [-Infinity, false, bound, true];
    default: return null;
  }
}

/** [variable, interval] for an inequality in one letter; null otherwise. */
function inequality(s: string): [string, Interval] | null {
  let v = s.replace(LATEX_INEQUALITY_RE, (op) => LATEX_INEQUALITY[op.slice(1, 3).toLowerCase()]);
  v = canonicalExpression(v.replace(/[−–]/g, "-")).replace(INEQUALITY_OPS_G, (op) => INEQUALITY_OP_NAMES[op] ?? op);
  const tokens = v.split(INEQUALITY_OPS).map((t) => INEQUALITY_OP_NAMES[t.trim()] ?? t.trim());

  if (tokens.length === 3) {
    const [left, op, right] = tokens;
    let bound: number | null;
    if (isVariable(left) && (bound = plainNumber(right)) !== null) {
      const r = ray(op, bound);
      return r ? [left, r] : null;
    }
    if (isVariable(right) && (bound = plainNumber(left)) !== null) {
      const r = ray(MIRRORED_OP[op], bound);
      return r ? [right, r] : null;
    }
    return null;
  }
  if (tokens.length === 5) {
    const [left, op1, variable, op2, right] = tokens;
    if (!isVariable(variable)) return null;
    const a = plainNumber(left);
    const b = plainNumber(right);
    if (a === null || b === null) return null;
    const up = (op: string) => op === "<" || op === "≤";
    const down = (op: string) => op === ">" || op === "≥";
    if (up(op1) && up(op2) && a < b) return [variable, [a, op1 === "≤", b, op2 === "≤"]];
    if (down(op1) && down(op2) && b < a) return [variable, [b, op2 === "≥", a, op1 === "≥"]];
  }
  return null;
}

const sameIntervals = (a: Interval[], b: Interval[]) =>
  a.length === b.length && a.every((x, i) => x.every((v, j) => v === b[i][j]));

/**
 * A one-variable inequality and its interval are the same answer, either way
 * round, and two intervals are one interval whatever their spacing. An
 * inequality key needs the same letter back; an interval key accepts any
 * letter — but only when it cannot be a point: „(2; 3)" with round brackets
 * and finite ends is just as likely a coordinate pair.
 */
function sameSolutionSet(userPlain: string, expectedPlain: string): boolean {
  const expectedIneq = inequality(expectedPlain);
  const expected = expectedIneq ? [expectedIneq[1]] : intervalUnion(expectedPlain);
  if (!expected) return false;

  const userIneq = inequality(userPlain);
  let user: Interval[] | null;
  if (userIneq) {
    if (expectedIneq && expectedIneq[0] !== userIneq[0]) return false;
    if (!expectedIneq && !unmistakableInterval(expected)) return false;
    user = [userIneq[1]];
  } else {
    user = intervalUnion(userPlain);
    if (!user) return false;
  }
  return sameIntervals(user, expected);
}

function unmistakableInterval(intervals: Interval[]): boolean {
  return (
    intervals.length > 1 ||
    intervals.some(([lo, loClosed, hi, hiClosed]) => loClosed || hiClosed || !Number.isFinite(lo) || !Number.isFinite(hi))
  );
}

/**
 * A sum of terms, for reordering: „-11y+18" is „18-11y" — mirrors
 * AnswerChecker.signed_terms. A term is a signed product of an optional
 * coefficient and letters or bracketed groups, each with an optional power,
 * compared as written: factors are never reordered, brackets never expanded.
 * A run of more than three letters is a word („kada-nors"), not a product.
 */
const TERM_RE = /^(?:\d+(?:[.,]\d+)?)?(?:[a-z](?:\^-?\d+)?|\([^()]+\)(?:\^-?\d+)?)*$/i;
const WORD_RUN_RE = /\p{L}{4,}/u;
/** „4-ame", „2-as": an ordinal ending, not a difference. */
const ORDINAL_IN_SUM_RE = /\d-\p{L}{2,}(?!\p{L})/u;

function signedTerms(str: string, insideBrackets = false): string[] | null {
  const s = canonicalExpression(str).replace(/[−–]/g, "-");
  if (!s || /\s/.test(s) || WORD_RUN_RE.test(s) || ORDINAL_IN_SUM_RE.test(s)) return null;

  const terms: string[] = [];
  let current = "";
  let depth = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if ((c === "+" || c === "-") && depth === 0 && i > 0 && s[i - 1] !== "^") {
      terms.push(current);
      current = c;
    } else {
      if (c === "(") depth++;
      if (c === ")") depth--;
      if (depth < 0) return null;
      current += c;
    }
  }
  terms.push(current);
  if (depth !== 0 || terms.length < 2) return null;

  const signed: [string, string][] = [];
  for (const term of terms) {
    const body = term.replace(/^[+-]/, "");
    if (!body || !TERM_RE.test(body)) return null;
    signed.push([term.startsWith("-") ? "-" : "+", body]);
  }
  if (!signed.some(([, body]) => /[a-z]/i.test(body))) return null;
  if (!insideBrackets && !signed.some(([, body]) => /[\d^]/.test(body))) return null;
  return signed.map(([sign, body]) => `${sign}|${body}`);
}

/** One factor of a product: a number, a letter or a bracketed group, with an
 *  optional power. */
const PRODUCT_FACTOR_RE = /\d+(?:[.,]\d+)?|[a-z](?:\^-?\d+)?|\([^()]+\)(?:\^-?\d+)?/gi;

/**
 * The factors of a product, for reordering: „4·(2b-3)" is the factors „4" and
 * „(2b-3)", and a bracketed group is itself a sum in any order, so
 * „(-3+2b)4" is the same product. An explicit multiplication sign next to a
 * letter or a bracket is the same as none. Null for anything that is not one
 * signed product, or that carries no digit, power or bracket: „ab" against
 * „ba" is too likely a word. Mirrors `product_factors` in answer_checker.rb.
 */
function productFactors(str: string): string | null {
  const s = canonicalExpression(str).replace(/[−–]/g, "-")
    .replace(/([a-z)])[*·×]/gi, "$1").replace(/[*·×]([a-z(])/gi, "$1");
  if (!s || /\s/.test(s) || WORD_RUN_RE.test(s) || !/[\d^(]/.test(s)) return null;

  const sign = s.startsWith("-") ? "-" : "+";
  const body = s.replace(/^[+-]/, "");
  const factors = body.match(PRODUCT_FACTOR_RE) ?? [];
  if (factors.join("") !== body || (factors.length < 2 && !body.startsWith("("))) return null;

  // Of a group and its negation the one sorting first is kept — „(b-a)" is
  // „-(a-b)" — and an even power swallows the sign taken out of it.
  let negative = sign === "-";
  const canonical = factors.map((factor) => {
    const m = factor.match(/^\(([^()]+)\)(\^-?\d+)?$/);
    if (!m) return factor.replace(",", ".");
    const terms = signedTerms(m[1], true);
    if (!terms) return `(${m[1]})${m[2] ?? ""}`;
    const asWritten = [...terms].sort().join(",");
    const negated = terms.map((t) => (t.startsWith("-|") ? "+" : "-") + t.slice(1)).sort().join(",");
    if (asWritten <= negated) return `(${asWritten})${m[2] ?? ""}`;
    if (!m[2] || parseInt(m[2].slice(1), 10) % 2 !== 0) negative = !negative;
    return `(${negated})${m[2] ?? ""}`;
  });
  return `${negative ? "-" : "+"}${canonical.sort().join("·")}`;
}

/** „(2b-3)·4" and „4(-3+2b)" for „4(2b-3)", „ba^2" for „a^2b": the same
 *  factors in another order. Never under exact_form. */
function sameProduct(userPlain: string, expectedPlain: string): boolean {
  const expected = productFactors(expectedPlain);
  if (!expected) return false;
  return productFactors(userPlain) === expected;
}

function unwrapBrackets(part: string): string {
  if (!(part.startsWith("(") && part.endsWith(")"))) return part;
  let depth = 0;
  for (let i = 0; i < part.length; i++) {
    if (part[i] === "(") depth++;
    if (part[i] === ")") depth--;
    if (depth === 0 && i < part.length - 1) return part;
  }
  return part.slice(1, -1);
}

function quotientParts(str: string): [string, string] | null {
  let depth = 0;
  let cut = -1;
  for (let i = 0; i < str.length; i++) {
    const c = str[i];
    if (c === "(") depth++;
    if (c === ")") depth--;
    if (c === "/" && depth === 0) {
      if (cut >= 0) return null;
      cut = i;
    }
  }
  if (cut < 0) return null;
  const raw = [str.slice(0, cut).trim(), str.slice(cut + 1).trim()];
  // „a+b/2" is „a + b/2": a bare sum on either side is not the numerator.
  if (raw.some(topLevelSum)) return null;
  // „2a/3b" is not safely „2a/(3b)": an unbracketed denominator is one number or one letter.
  if (!raw[1].startsWith("(") && !/^\d+(?:[.,]\d+)?$|^[a-z](?:\^\d+)?$/i.test(raw[1])) return null;
  const parts = raw.map(unwrapBrackets);
  return parts.some((p) => !p) ? null : [parts[0], parts[1]];
}

function topLevelSum(part: string): boolean {
  let depth = 0;
  for (let i = 0; i < part.length; i++) {
    const c = part[i];
    if (c === "(") depth++;
    if (c === ")") depth--;
    if ("+-−".includes(c) && depth === 0 && i > 0 && part[i - 1] !== "^") return true;
  }
  return false;
}

/** „(2+3x)/x^2" for „(3x+2)/(x^2)" — mirrors AnswerChecker.same_quotient?. */
function sameQuotient(userPlain: string, expectedPlain: string, rules?: AnswerRules | null): boolean {
  const expected = quotientParts(expectedPlain);
  if (!expected) return false;
  const user = quotientParts(userPlain);
  if (!user) return false;
  return matchValue(user[0], expected[0], rules) && matchValue(user[1], expected[1], rules);
}

/** A factorisation into whole numbers as a sorted multiset — mirrors
 *  AnswerChecker.number_factors. */
function numberFactors(str: string): number[] | null {
  const parts = superscriptsAsPowers(str).replace(/ /g, "").split(/[*·×]/);
  if (parts.length < 2 && !(parts[0] ?? "").includes("^")) return null;
  const factors: number[] = [];
  for (const part of parts) {
    const m = part.match(/^(\d+)(?:\^(\d+))?$/);
    if (!m) return null;
    const times = m[2] === undefined ? 1 : parseInt(m[2], 10);
    for (let i = 0; i < times; i++) factors.push(parseInt(m[1], 10));
  }
  return factors.length >= 2 ? factors.sort((a, b) => a - b) : null;
}

/** „3·2·2" and „2²·3" for „2·2·3". Never under exact_form. */
function sameFactorisation(userPlain: string, expectedPlain: string): boolean {
  const expected = numberFactors(expectedPlain);
  if (!expected) return false;
  const user = numberFactors(userPlain);
  return !!user && user.length === expected.length && user.every((v, i) => v === expected[i]);
}

/** The same signed terms in another order. Never on an equation or anything
 *  that is not plainly a sum of terms. */
function sameTerms(userPlain: string, expectedPlain: string): boolean {
  // An explicit „+" or a leading „−" is algebra even with no digit — mirrors same_terms?.
  const loose = /^-|.\+/.test(userPlain);
  const expected = signedTerms(expectedPlain, loose);
  if (!expected) return false;
  const user = signedTerms(userPlain, loose);
  if (!user) return false;
  return user.length === expected.length && [...user].sort().join("\n") === [...expected].sort().join("\n");
}

/**
 * The roots of an equation are a set, not a sequence — mirrors
 * AnswerChecker.same_roots?. „x₁ = 3, x₂ = -3" is answered by „-3; 3",
 * „3 ir -3", „x = -3 arba x = 3" or „±3". The key has to say they are roots —
 * every item labelled with one letter, indexed 1, 2, … or not at all — because
 * a bare „3; -3" is as likely a coordinate pair, whose order is the answer. A
 * bare key gains only the labelled spelling, in its own order.
 */
const ROOT_LABEL_RE = /^([a-z])\s*(?:_?\{?([1-9])\}?|([₁-₉]))?\s*=\s*/i;
const ROOT_INDEX: Record<string, string> = Object.fromEntries(
  ["₁", "₂", "₃", "₄", "₅", "₆", "₇", "₈", "₉"].map((c, i) => [c, String(i + 1)])
);
const ROOT_SPLIT_RE = /\s*;\s*|,\s*(?=[a-z])|,\s+|\s+(?:ir|arba)\s+/i;
const PLUS_MINUS_RE = /^(?:([a-z])\s*(?:_?\{?1,?2\}?|₁,?₂)?\s*=\s*)?(?:±|\+-|\+\/-|\\pm)\s*(.+)$/i;

type Roots = { values: number[]; letter: string | null; plusMinus: boolean };

function roots(str: string, splits: RegExp[]): Roots | null {
  const s = str.trim().replace(/[−–]/g, "-");
  const pm = s.match(PLUS_MINUS_RE);
  if (pm) {
    const value = plainNumber(pm[2]);
    return value !== null && value > 0
      ? { values: [value, -value], letter: pm[1]?.toLowerCase() ?? null, plusMinus: true }
      : null;
  }

  for (const re of splits) {
    const items = s.split(re);
    if (items.length < 2) continue;

    const parsed = items.map((item) => {
      const label = item.trim().match(ROOT_LABEL_RE);
      return [label, plainNumber(label ? item.trim().slice(label[0].length) : item)] as const;
    });
    if (!parsed.every(([, value]) => value !== null)) continue;

    const labels = parsed.map(([label]) => label);
    const values = parsed.map(([, value]) => value as number);
    if (labels.every((l) => l === null)) return { values, letter: null, plusMinus: false };
    if (!labels.every((l) => l !== null)) continue;

    const letters = new Set(labels.map((l) => l![1].toLowerCase()));
    const indices = labels.map((l) => l![2] ?? (l![3] !== undefined ? ROOT_INDEX[l![3]] : null));
    if (letters.size !== 1) continue;
    const expectedIndices = indices.map((_, i) => String(i + 1));
    if (!indices.every((i) => i === null) && [...indices].sort().join() !== expectedIndices.join()) continue;

    return { values, letter: [...letters][0], plusMinus: false };
  }
  return null;
}

const sameValues = (a: number[], b: number[]) => a.every((x, i) => Math.abs(x - b[i]) < 0.0001);

function sameRoots(userPlain: string, expectedPlain: string): boolean {
  const expected = roots(expectedPlain, [ROOT_SPLIT_RE]);
  if (!expected) return false;
  const user = roots(userPlain, [ROOT_SPLIT_RE, /\s+/]);
  if (!user || user.values.length !== expected.values.length) return false;

  if (expected.letter) {
    if (user.letter && user.letter !== expected.letter) return false;
    const byValue = (a: number, b: number) => a - b;
    return sameValues([...user.values].sort(byValue), [...expected.values].sort(byValue));
  }
  return !!user.letter && !user.plusMinus && sameValues(user.values, expected.values);
}

/**
 * A set written in braces — mirrors AnswerChecker.same_set?. „{t, a}" is
 * „{a,t}", „{-1, 0}" is „{0;-1}" — the elements in any order, each compared by
 * the ordinary rules — and „∅" is „{}". A set is never its element: „2" is not
 * „{2}".
 */
const SET_RE = /^\{(.*)\}$/;
const EMPTY_SET_RE = /^(?:∅|\\emptyset|\\varnothing|\{\s*\}|tuščioji aibė|tuščia aibė)$/i;

/** The inside of a set in braces ("" for the empty set); null for anything
 *  that is not one set — including a set holding another. */
function setInner(str: string): string | null {
  const s = str.trim().replace(/\\([{}])/g, "$1");
  if (EMPTY_SET_RE.test(s)) return "";
  const m = s.match(SET_RE);
  if (!m) return null;
  const inner = m[1].trim();
  return /[{}]/.test(inner) ? null : inner;
}

function sameSet(userPlain: string, expectedPlain: string, rules?: AnswerRules | null): boolean {
  const expectedInner = setInner(expectedPlain);
  if (expectedInner === null) return false;
  // Two or more elements typed without braces are still the set the key
  // names; a single one is an element, never a set.
  const userInner = setInner(userPlain) ?? (/[{}]/.test(userPlain) || !/[,;]/.test(userPlain) ? null : userPlain.trim());
  if (userInner === null) return false;
  if (!expectedInner || !userInner) return expectedInner === userInner;

  const keySplit = expectedInner.includes(";")
    ? SEMICOLON_SPLIT_RE
    : /,\s/.test(expectedInner)
      ? LIST_SPLIT_RE
      : /\s*,\s*/;
  const expected = expectedInner.split(keySplit).map(normalize);
  const splits = userInner.includes(";") ? [SEMICOLON_SPLIT_RE] : [LIST_SPLIT_RE];
  if (!userInner.includes(";") && !expected.some((e) => e.includes(","))) splits.push(/\s*,\s*/);

  return splits.some((re) => {
    const items = userInner.split(re).map(normalize);
    if (items.length !== expected.length) return false;
    const remaining = [...expected];
    return items.every((item) => {
      const i = remaining.findIndex((e) => matchSingle(item, e, rules));
      if (i < 0) return false;
      remaining.splice(i, 1);
      return true;
    });
  });
}

/**
 * A count up to ten written as a word — mirrors AnswerChecker::NUMBER_WORDS:
 * „du", „dvi sprendinius", „vieną", „nulis", „nė vieno" for „2", „1", „0".
 * Every case and gender, optionally followed by one word. Only against a key
 * that is a bare whole number 0–10 and an answer with no digits.
 */
const NUMBER_WORDS: Record<number, string[]> = {
  0: ["nulis", "nulio", "nuliui", "nulį", "nuliu", "nulyje",
    "nė vieno", "nė vienos", "nė vienas", "nė viena", "nė vieną", "nė vienam", "nė vienai"],
  1: ["vienas", "vieno", "vienam", "vieną", "vienu", "viename", "viena", "vienos", "vienai", "vienoje"],
  2: ["du", "dvi", "dviejų", "dviem", "dviese"],
  3: ["trys", "trijų", "trims", "tris", "trimis", "trijuose", "trise"],
  4: ["ketur"], 5: ["penk"], 6: ["šeš"], 7: ["septyn"], 8: ["aštuon"], 9: ["devyn"],
  10: ["dešimt", "dešimties", "dešimčiai", "dešimčia", "dešimtyje"],
};
const NUMBER_WORD_ENDINGS = ["i", "ios", "ių", "iems", "ioms", "is", "ias", "iais", "iomis", "iuose", "iose", "iese"];
const COUNT_KEY_RE = /^(?:10|\d)$/;

function numberWords(rules?: AnswerRules | null): Map<string, number> {
  const table = new Map<string, number>();
  for (const [key, stems] of Object.entries(NUMBER_WORDS)) {
    const value = Number(key);
    const forms = value >= 4 && value <= 9 ? stems.flatMap((stem) => NUMBER_WORD_ENDINGS.map((e) => stem + e)) : stems;
    for (const form of forms) table.set(prepare(form, rules), value);
  }
  return table;
}

function numberWordValue(s: string, rules?: AnswerRules | null): number | null {
  if (/\d/.test(s)) return null;
  const words = s.split(" ");
  const table = numberWords(rules);
  for (const n of [2, 1]) {
    if (words.length < n || words.length > n + 1) continue;
    const value = table.get(words.slice(0, n).join(" "));
    if (value === undefined) continue;
    if (words.length === n || /^\p{L}+$/u.test(words[words.length - 1])) return value;
  }
  return null;
}

/**
 * The solution of a system, each value labelled with its own letter — mirrors
 * AnswerChecker.same_system?. „x=2; y=3" is „y = 3, x = 2"; a bare pair
 * („(2; 3)") answers it in alphabetical order of the letters.
 */
const SYSTEM_LABEL_RE = /^([a-z])\s*=\s*/i;

function labelledValues(str: string): Map<string, number> | null {
  const items = str.trim().split(ROOT_SPLIT_RE);
  if (items.length < 2) return null;
  const pairs = new Map<string, number>();
  for (const raw of items) {
    const item = raw.trim();
    const m = item.match(SYSTEM_LABEL_RE);
    if (!m) return null;
    const value = plainNumber(item.slice(m[0].length));
    if (value === null) return null;
    const letter = m[1].toLowerCase();
    if (pairs.has(letter)) return null;
    pairs.set(letter, value);
  }
  return pairs;
}

function sameSystem(userPlain: string, expectedPlain: string): boolean {
  const expected = labelledValues(expectedPlain);
  if (!expected) return false;
  const user = labelledValues(userPlain);
  if (user) {
    if ([...user.keys()].sort().join() !== [...expected.keys()].sort().join()) return false;
    return [...expected].every(([letter, value]) => Math.abs((user.get(letter) as number) - value) < 0.0001);
  }

  const ordered = [...expected.keys()].sort().map((k) => expected.get(k) as number);
  const bare = unwrapParens(userPlain);
  const splits = bare.includes(";") ? [SEMICOLON_SPLIT_RE] : [LIST_SPLIT_RE];
  if (!bare.includes(";") && ordered.every((v) => v === Math.round(v))) splits.push(/\s*,\s*/);
  return splits.some((re) => {
    const items = bare.split(re);
    return (
      items.length === ordered.length &&
      items.every((item, i) => {
        const n = plainNumber(item);
        return n !== null && Math.abs(n - ordered[i]) < 0.0001;
      })
    );
  });
}

/**
 * A ratio is a fraction — mirrors AnswerChecker.same_ratio?: „1:4" is „1/4" is
 * „0,25", only against a key that is itself a fraction or ratio, never against
 * a whole number, never two ratios of different terms, and never a key that
 * reads as a clock time.
 */
const RATIO_RE = /^(-?\d+(?:[.,]\d+)?)\s*:\s*(\d+(?:[.,]\d+)?)$/;
const CLOCK_LIKE_RE = /^\d{1,2}\s*:\s*\d{2}$/;

/** A rate is a percentage or the same fraction of one: „8%" is „0,08" — only
 *  for a key that is one percentage, or one decimal below 1. Mirrors
 *  AnswerChecker.same_rate?. */
const PERCENT_RE = /^(.+?)\s*%$/;

function percentValue(s: string): number | null {
  const m = s.trim().match(PERCENT_RE);
  return m ? plainNumber(m[1]) : null;
}

function sameRate(userPlain: string, expectedPlain: string): boolean {
  const percent = percentValue(expectedPlain);
  if (percent !== null) {
    const user = plainNumber(userPlain);
    return user !== null && Math.abs(user * 100 - percent) < 0.0001;
  }
  const decimal = plainNumber(expectedPlain);
  if (decimal === null || Math.abs(decimal) >= 1 || decimal === Math.round(decimal)) return false;
  const user = percentValue(userPlain);
  return user !== null && Math.abs(user - decimal * 100) < 0.0001;
}

function ratioValue(s: string): number | null {
  const m = collapseThousands(s.trim()).match(RATIO_RE);
  if (!m) return null;
  const a = plainNumber(m[1]);
  const b = plainNumber(m[2]);
  return a !== null && b !== null && b !== 0 ? a / b : null;
}

function sameRatio(userPlain: string, expectedPlain: string): boolean {
  if (CLOCK_LIKE_RE.test(expectedPlain)) return false;

  const expectedRatio = ratioValue(expectedPlain);
  const expected = expectedRatio ?? (expectedPlain.includes("/") ? plainNumber(expectedPlain) : null);
  if (expected === null) return false;

  const userRatio = ratioValue(userPlain);
  if (expectedRatio !== null && userRatio !== null) return false;
  if (userRatio === null && expectedRatio === null) return false;

  const user = userRatio ?? plainNumber(userPlain);
  if (user === null) return false;
  if (userRatio === null && !userPlain.includes("/") && user === Math.round(user)) return false;
  return Math.abs(user - expected) < 0.0001;
}

/** „15 = x + 7" for „x+7=15": the sides of one equation, swapped, each written
 *  the same. Mirrors AnswerChecker.swapped_equation?. */
function swappedEquation(userTidy: string, expectedTidy: string): boolean {
  if (/[<>≤≥]/.test(userTidy + expectedTidy)) return false;
  const expected = expectedTidy.split("=");
  const user = userTidy.split("=");
  return (
    expected.length === 2 &&
    user.length === 2 &&
    expected.every((side) => side !== "") &&
    user[0] === expected[1] &&
    user[1] === expected[0]
  );
}

/**
 * A number written with roots or π, compared by value against a key written
 * the same way — mirrors AnswerChecker.same_numeric_expression?. Both sides
 * must hold √ or π, and the answer may not use more operators than the key.
 */
const NUMERIC_EXPRESSION_RE = /^[\d.,+\-×·*/^√π()]+$/;
const NUMERIC_TOKEN_RE = /\d+(?:[.,]\d+)?|[+\-×·*/^√π()]/g;
const NUMERIC_TOLERANCE = 1e-9;

function sameNumericExpression(userTidy: string, expectedTidy: string): boolean {
  const expected = numericExpression(expectedTidy);
  if (!expected) return false;
  const user = numericExpression(userTidy);
  if (!user || user[1] > expected[1]) return false;
  const [a, b] = [user[0], expected[0]];
  return a === b || Math.abs(a - b) <= NUMERIC_TOLERANCE * Math.max(Math.abs(a), Math.abs(b));
}

function numericExpression(str: string): [number, number] | null {
  const s = str.replace(/ /g, "").replace(/−/g, "-");
  if (!NUMERIC_EXPRESSION_RE.test(s) || !/[√π]/.test(s)) return null;
  const tokens = s.match(NUMERIC_TOKEN_RE) ?? [];
  if (tokens.join("") !== s) return null;
  return evaluateNumericExpression(tokens);
}

/** Recursive descent, token for token the same as AnswerChecker::NumericExpression. */
function evaluateNumericExpression(tokens: string[]): [number, number] | null {
  let i = 0;
  let operators = 0;
  const peek = () => tokens[i];
  const take = () => tokens[i++];
  const startsPrimary = (t: string | undefined) => t !== undefined && (/^\d/.test(t) || t === "π" || t === "√" || t === "(");

  function expr(): number | null {
    let value = term();
    if (value === null) return null;
    while (peek() === "+" || peek() === "-") {
      const op = take();
      operators++;
      const right = term();
      if (right === null) return null;
      value = op === "+" ? value + right : value - right;
    }
    return value;
  }
  function term(): number | null {
    let value = unary();
    if (value === null) return null;
    for (;;) {
      const t = peek();
      if (t === "×" || t === "·" || t === "*" || t === "/") {
        const op = take();
        operators++;
        const right = unary();
        if (right === null) return null;
        if (op === "/" && right === 0) return null;
        value = op === "/" ? value / right : value * right;
      } else if (startsPrimary(t)) {
        const right = power();
        if (right === null) return null;
        value *= right;
      } else {
        break;
      }
    }
    return value;
  }
  function unary(): number | null {
    if (peek() !== "-") return power();
    take();
    const value = unary();
    return value === null ? null : -value;
  }
  function power(): number | null {
    const base = primary();
    if (base === null) return null;
    if (peek() !== "^") return base;
    take();
    operators++;
    const exponent = unary();
    if (exponent === null) return null;
    if (base < 0 && exponent !== Math.round(exponent)) return null;
    return base ** exponent;
  }
  function primary(): number | null {
    const t = peek();
    if (t === undefined) return null;
    if (t === "π") {
      take();
      return Math.PI;
    }
    if (t === "√") {
      take();
      const value = primary();
      return value !== null && value >= 0 ? Math.sqrt(value) : null;
    }
    if (t === "(") {
      take();
      const value = expr();
      return value !== null && take() === ")" ? value : null;
    }
    if (!/^\d/.test(t)) return null;
    take();
    return Number(t.replace(",", "."));
  }

  const value = expr();
  return value !== null && i === tokens.length && Number.isFinite(value) ? [value, operators] : null;
}

/** The written form `exactForm` compares: notation forgiven, value not. */
function writtenForm(s: string): string {
  return canonicalExpression(superscriptsAsPowers(normalize(stripKatex(s))))
    .replace(/(\d),(\d)/g, "$1.$2")
    .replace(/\s*([;()[\]∪])\s*/g, "$1");
}

/**
 * A key that is a list of numbers — items split on „; " or on a comma followed
 * by a space, since a bare comma is the Lithuanian decimal point.
 */
const LIST_SPLIT_RE = /\s*;\s*|,\s+/;
/** A pair or tuple may be wrapped in round brackets („(8; 11)"), on either
 *  side, and an answer holding „;" is split on „;" alone, which is the whole
 *  reason Lithuanian writes „(2,5; 3)": its commas belong to the numbers. */
const SEMICOLON_SPLIT_RE = /\s*;\s*/;
const PARENS_RE = /^\(\s*(.*?)\s*\)$/;

function unwrapParens(s: string): string {
  const m = s.match(PARENS_RE);
  return m ? m[1] : s;
}

function numberList(s: string): number[] | null {
  const items = s.split(LIST_SPLIT_RE);
  if (items.length < 2) return null;
  const values = items.map(parseNumeric);
  return values.every((v) => v !== null) ? (values as number[]) : null;
}

/**
 * „761,671,167" and „761 671 167" for a key „761, 671, 167": the same numbers
 * in the same order, separated by commas, semicolons or spaces — mirrors
 * AnswerChecker.same_number_list?. A bare comma splits the answer only when
 * every number in the key is whole — otherwise „3,5" in the answer has to stay
 * one number. Order counts: „7, 5" is not „5, 7".
 */
/** A point or a vector may be named with its coordinates: „B(7; -8)" and
 *  „AB = (4; 3)" are the bare pairs, and a unit after the pair („(3; 4) m/s")
 *  is dropped — but where both sides carry one it must be the same. Mirrors
 *  AnswerChecker::POINT_LABEL_RE / PAIR_UNIT_RE. */
const POINT_LABEL_RE = /^[a-z]{1,2}\s*=?\s*(?=\()/i;
const PAIR_UNIT_RE = /(?<=\))\s*([\p{L}°][\p{L}/²³.]*)$/u;

function pairAndUnit(s: string): [string, string | null] {
  const v = s.replace(POINT_LABEL_RE, "");
  const m = v.match(PAIR_UNIT_RE);
  return m ? [v.slice(0, m.index), m[1].replace(/\.$/, "")] : [v, null];
}

/** „(6;6√3)" and „6; 6√3" for „(6; 6√3)": a pair whose items are not plain
 *  numbers, compared item by item as written, in order, spacing and brackets
 *  forgiven. Mirrors AnswerChecker.same_pair_terms?. */
function pairItem(item: string): string {
  return canonicalExpression(item.replace(/\s+/g, "")).replace(/,/g, ".");
}

function samePairTerms(userPlain: string, expectedPlain: string): boolean {
  const [expectedPair, expectedUnit] = pairAndUnit(expectedPlain);
  const [userPair, userUnit] = pairAndUnit(userPlain);
  if (expectedUnit !== null && userUnit !== null && expectedUnit !== userUnit) return false;

  const expected = unwrapParens(expectedPair);
  if (!expected.includes(";")) return false;
  const expectedItems = expected.split(SEMICOLON_SPLIT_RE);
  const user = unwrapParens(userPair);
  const userItems = user.split(user.includes(";") ? SEMICOLON_SPLIT_RE : /\s*,\s+/);
  if (expectedItems.length < 2 || userItems.length !== expectedItems.length) return false;
  if (!expectedItems.every((e) => /\d/.test(e))) return false;
  return userItems.every((u, i) => pairItem(u) === pairItem(expectedItems[i]));
}

function sameNumberList(userPlain: string, expectedPlain: string): boolean {
  const [expectedPair, expectedUnit] = pairAndUnit(expectedPlain);
  const [userPair, userUnit] = pairAndUnit(userPlain);
  if (expectedUnit !== null && userUnit !== null && expectedUnit !== userUnit) return false;

  const expected = numberList(unwrapParens(expectedPair));
  if (!expected) return false;

  const user = unwrapParens(userPair);
  let splits: RegExp[];
  if (user.includes(";")) {
    splits = [SEMICOLON_SPLIT_RE];
  } else {
    splits = [LIST_SPLIT_RE, /\s+/];
    if (expected.every((v) => v === Math.round(v))) splits.push(/\s*,\s*|\s+/);
  }
  return splits.some((re) => {
    const items = user.split(re);
    if (items.length !== expected.length) return false;
    return items.every((item, i) => {
      const n = parseNumeric(item);
      return n !== null && Math.abs(n - expected[i]) < 0.0001;
    });
  });
}

/**
 * A lone variable on the left of "=": "x = 8", "y=8", "a_1 = 5", "x₂ = 5",
 * "P = 4a".
 * The "=" must follow the variable directly, so an answer that is itself an
 * equation ("50 - x = 35", "24 : n = 6") keeps both sides and is compared
 * whole — there the equation is the thing being asked for.
 */
const VAR_PREFIX_RE = /^[a-z](?:_?\d|[₀-₉])?\s*=\s*(?=\S)/i;

/**
 * Wider labels a typed answer may carry in front of its value — a term with a
 * power („c² = 24"), up to three letters („BC = 5"), or a function of one
 * letter or number („cos C = 1/8", „lg 15 = 1 - a + b") — dropped from the ANSWER only, and only when the key
 * is a bare value. Mirrors AnswerChecker::ANSWER_LABEL_RE.
 */
const ANSWER_LABEL_RE =
  /^(?:(?:sin|cos|tg|tan|ctg|cot|lg|ln|log(?:_?\d+)?)\s*\(?\s*(?:\p{L}|\d+(?:[.,]\d+)?°?)\s*\)?|\p{L}{1,3}(?:\^\d+|[²³])?|[a-z](?:_?\d|[₀-₉]))\s*=\s*(?=\S)/iu;

function answerLabelValue(userNorm: string, expectedNorm: string, rules?: AnswerRules | null): string | null {
  if (rules?.exactForm || stripKatex(expectedNorm).includes("=")) return null;
  const plain = superscriptsAsPowers(normalize(stripKatex(userNorm)));
  if (!ANSWER_LABEL_RE.test(plain)) return null;
  const value = normalize(plain.replace(ANSWER_LABEL_RE, ""));
  return !value || value.includes("=") ? null : value;
}

/** "x = 8" -> "8"; null when there is no lone-variable prefix to drop. */
function varPrefixValue(s: string): string | null {
  const plain = normalize(stripKatex(s));
  if (!VAR_PREFIX_RE.test(plain)) return null;
  const value = normalize(plain.replace(VAR_PREFIX_RE, ""));
  return value || null;
}

/**
 * A child who solves "2x + 7 = 23" and writes "8" answered the question;
 * so did one who wrote "x = 8" where the key says "8". Either side may carry
 * the variable, so both are dropped and the values compared.
 */
/**
 * A whole sentence typed without its full stop, or with one the key leaves out
 * — mirrors `AnswerChecker.same_sentence_but_full_stop?`. Sentences only (the
 * key holds a space), and only the final ".": "?" or "!" still differ.
 */
function sameSentenceButFullStop(userNorm: string, expectedNorm: string): boolean {
  if (!expectedNorm.includes(" ")) return false;
  const bare = (s: string) => (s.endsWith(".") ? s.slice(0, -1) : s).trimEnd();
  return bare(userNorm) === bare(expectedNorm) && userNorm !== expectedNorm;
}

function matchSingle(userNorm: string, expectedNorm: string, rules?: AnswerRules | null): boolean {
  if (matchValue(userNorm, expectedNorm, rules)) return true;
  if (sameSentenceButFullStop(userNorm, expectedNorm)) return true;

  const userBare = varPrefixValue(userNorm);
  const expectedBare = varPrefixValue(expectedNorm);
  if (userBare !== null || expectedBare !== null) {
    if (
      matchValue(userBare ?? normalize(stripKatex(userNorm)), expectedBare ?? normalize(stripKatex(expectedNorm)), rules)
    ) {
      return true;
    }
  }

  const labelled = answerLabelValue(userNorm, expectedNorm, rules);
  return labelled !== null && matchValue(labelled, normalize(stripKatex(expectedNorm)), rules);
}

function matchValue(userNorm: string, expectedNorm: string, rules?: AnswerRules | null): boolean {
  // 1. Exact match (after normalization)
  if (userNorm === expectedNorm) return true;
  if (rules?.exactForm) return writtenForm(userNorm) === writtenForm(expectedNorm);

  // 2. KaTeX tolerance: strip $\frac{1}{3}$ to 1/3, then compare
  const userPlain = normalize(stripKatex(userNorm));
  const expectedPlain = normalize(stripKatex(expectedNorm));
  if (userPlain === expectedPlain) return true;
  if (canonicalExpression(userPlain) === canonicalExpression(expectedPlain)) return true;

  // Superscript powers and redundant brackets, then the readings that do not
  // depend on how the answer is laid out: a solution set, the terms of a sum
  // in any order, the roots of an equation.
  const userTidy = tidyExpression(userPlain);
  const expectedTidy = tidyExpression(expectedPlain);
  if (userTidy === expectedTidy) return true;
  if (swappedEquation(userTidy, expectedTidy)) return true;
  if (sameNumericExpression(userTidy, expectedTidy)) return true;
  if (sameSolutionSet(userPlain, expectedPlain)) return true;
  if (sameTerms(userTidy, expectedTidy)) return true;
  if (sameProduct(userTidy, expectedTidy)) return true;
  if (sameFactorisation(userPlain, expectedPlain)) return true;
  if (sameQuotient(userTidy, expectedTidy, rules)) return true;
  if (sameRoots(userPlain, expectedPlain)) return true;
  if (sameSet(userPlain, expectedPlain, rules)) return true;
  if (sameSystem(userPlain, expectedPlain)) return true;

  // 3. Clock times and durations, which are compared as times however they
  //    are written, then quantities in two units of one dimension (converted;
  //    two dimensions never match), then numeric equivalence (on the
  //    KaTeX-stripped values).
  if (sameTime(userPlain, expectedPlain)) return true;

  const duration = sameDuration(userPlain, expectedPlain);
  if (duration !== null) return duration;

  const quantity = sameQuantity(userPlain, expectedPlain);
  if (quantity !== null) return quantity;

  const userNum = parseNumeric(userPlain);
  const expectedNum = parseNumeric(expectedPlain);
  if (userNum !== null && expectedNum !== null) {
    // Use epsilon for floating point comparison
    if (Math.abs(userNum - expectedNum) < 0.0001) return true;
  }
  if (sameNumberList(userPlain, expectedPlain)) return true;
  if (samePairTerms(userPlain, expectedPlain)) return true;
  if (sameRatio(userPlain, expectedPlain)) return true;
  if (sameRate(userPlain, expectedPlain)) return true;
  if (COUNT_KEY_RE.test(expectedPlain) && numberWordValue(userPlain, rules) === Number(expectedPlain)) return true;

  // 4. Unit tolerance: strip units and compare
  const userStripped = stripUnits(userPlain);
  const expectedStripped = stripUnits(expectedPlain);
  if (userStripped && expectedStripped && userStripped !== userPlain) {
    if (userStripped === expectedStripped) return true;
    if (tidyExpression(userStripped) === tidyExpression(expectedStripped)) return true;
    // Also try numeric comparison after stripping units
    const userStrippedNum = parseNumeric(userStripped);
    const expectedStrippedNum = parseNumeric(expectedStripped);
    if (
      userStrippedNum !== null &&
      expectedStrippedNum !== null &&
      Math.abs(userStrippedNum - expectedStrippedNum) < 0.0001
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Advanced answer checking.
 * Expected answer may contain multiple acceptable answers separated by ";;".
 * Returns "CORRECT" or "INCORRECT".
 */
export function checkAnswerAdvanced(
  userAnswer: string | null | undefined,
  expectedAnswer: string | null | undefined,
  rules?: AnswerRules | null
): CheckResult {
  if (!userAnswer?.trim() || !expectedAnswer?.trim()) return "INCORRECT";

  if (rules?.form) {
    const written = inForm(userAnswer, rules.form);
    if (written === null) return "INCORRECT";
    userAnswer = written;
  }
  const userNorm = prepare(userAnswer, rules);

  // Split on ";;" for multiple acceptable answers
  const acceptableAnswers = expectedAnswer.split(";;").map((a) => prepare(a, rules));

  for (const acceptable of acceptableAnswers) {
    if (matchSingle(userNorm, acceptable, rules)) return "CORRECT";
  }

  return "INCORRECT";
}

/**
 * A picked choice, graded by identity rather than by resemblance — mirrors
 * `AnswerChecker.check_choice`. Every tolerance in `checkAnswerAdvanced` forgives
 * typing, and a tapped button typed nothing: with ą / a as the options, "a"
 * stripped to the key and was graded correct. Null when the answer is not one of
 * the choices, so the caller falls back to the tolerant check.
 */
export function checkChoiceAnswer(
  userAnswer: string | null | undefined,
  expectedAnswer: string | null | undefined,
  choices: string[] | null | undefined,
  rules?: AnswerRules | null
): CheckResult | null {
  if (!choices?.length || !userAnswer?.trim() || !expectedAnswer?.trim()) return null;

  // Case is forgiven only while no two choices differ by case alone ("jums" /
  // "Jums" on a capitalisation question), or the question says it counts —
  // mirrors the server. Marks always count on a tapped choice.
  const lower = (v: string) => normalize(v).toLowerCase();
  const caseMatters =
    !!rules?.caseSensitive || new Set(choices.map(lower)).size < new Set(choices.map(normalize)).size;
  const key = caseMatters ? normalize : lower;

  const picked = key(userAnswer);
  if (!choices.some((c) => key(c) === picked)) return null;

  const denoted = expectedAnswer
    .split(";;")
    .map((a) => denotedChoice(a, choices, key))
    .filter((c): c is string => c !== null);
  return denoted.includes(picked) ? "CORRECT" : "INCORRECT";
}

/**
 * Which choice a key names — mirrors `AnswerChecker.denoted_choice`. A key
 * written as one of the choices names that one. A key written without the
 * KaTeX its choice carries („1/4" over a choice „$\frac{1}{4}$") names the
 * choice it equals once both are stripped to plain text — but only when
 * exactly one choice does, so stripping can never make a key name two
 * choices, or one choice stand for another.
 */
function denotedChoice(answer: string, choices: string[], key: (v: string) => string): string | null {
  const exact = key(answer);
  if (choices.some((c) => key(c) === exact)) return exact;

  const plain = (v: string) => tidyExpression(normalize(stripKatex(key(v))));
  const target = plain(answer);
  const hits = [...new Set(choices.map(key))].filter((c) => plain(c) === target);
  return hits.length === 1 ? hits[0] : null;
}

/**
 * True when the question enumerates its Nth sub-question as "a)", "b)", "c)".
 *
 * The marker has to start a list item — beginning of the text, after a space,
 * or after a tag (`<br>a)`). Anything else that ends in a letter and a bracket
 * is not an enumeration: "(branduolys)", "f(a)", "[a; b]" and "(a-b)" all carry
 * a letter before ")" and none of them announces a second answer field.
 */
function hasEnumerationMarker(problemText: string, index: number): boolean {
  const letter = String.fromCharCode(97 + index);
  return new RegExp(`(?:^|[\\s>])${letter}\\)`).test(problemText);
}

/**
 * Detect if a problem has multiple sub-answers (e.g., "52, 34, 82").
 * Splits on ", " (comma + space). Decimal numbers like "0,25" have no space
 * after the comma, so they won't be incorrectly split.
 *
 * Returns the array of expected sub-answers, or null if it's a single-answer problem.
 *
 * Every part must have its own marker in the question — an answer of three parts
 * needs an a), a b) and a c). A comma in an answer is far commoner than an
 * enumeration ("(5, 5)" is one coordinate, "284, 408, 428" is one sorted list),
 * so anything short of the full set of markers is one answer that happens to
 * contain a comma.
 *
 * A multiple-choice answer is never split: it is whatever the student clicked,
 * word for word, commas and all. Splitting it hands them fields they cannot
 * type into while their click goes nowhere.
 */
export function detectMultiAnswers(
  expectedAnswer: string | null | undefined,
  problemText: string | null | undefined,
  choices?: string[] | null
): string[] | null {
  if (!expectedAnswer || !problemText) return null;
  if (choices && choices.length > 0) return null;

  // Split on ", " (comma followed by space)
  const parts = expectedAnswer.split(", ");
  if (parts.length < 2) return null;

  if (!parts.every((_, i) => hasEnumerationMarker(problemText, i))) return null;

  return parts;
}

/**
 * Check multiple sub-answers independently.
 * Returns per-sub-answer results and an overall result.
 */
export function checkMultiAnswerAdvanced(
  userAnswers: string[],
  expectedAnswers: string[],
  rules?: AnswerRules | null
): { results: CheckResult[]; overall: MultiCheckResult } {
  const results: CheckResult[] = expectedAnswers.map((expected, i) => {
    const userAnswer = userAnswers[i] || "";
    return checkAnswerAdvanced(userAnswer, expected, rules);
  });

  const correctCount = results.filter((r) => r === "CORRECT").length;

  let overall: MultiCheckResult;
  if (correctCount === results.length) {
    overall = "CORRECT";
  } else if (correctCount === 0) {
    overall = "INCORRECT";
  } else {
    overall = "PARTIAL";
  }

  return { results, overall };
}

/**
 * Parse {_+} blank markers from question text.
 * Returns array of objects with the start index and underscore count for each blank.
 */
export function parseFillBlanks(questionHtml: string): { index: number; length: number }[] {
  const blanks: { index: number; length: number }[] = [];
  const regex = /\{(_+)\}/g;
  let match;
  while ((match = regex.exec(questionHtml)) !== null) {
    blanks.push({ index: match.index, length: match[1].length });
  }
  return blanks;
}

/**
 * Split question HTML into segments of text and blanks for rendering.
 * Returns alternating text and blank segments.
 */
export function splitFillBlanks(questionHtml: string): Array<{ type: "text"; value: string } | { type: "blank"; length: number }> {
  const segments: Array<{ type: "text"; value: string } | { type: "blank"; length: number }> = [];
  const regex = /\{(_+)\}/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(questionHtml)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "text", value: questionHtml.slice(lastIndex, match.index) });
    }
    segments.push({ type: "blank", length: match[1].length });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < questionHtml.length) {
    segments.push({ type: "text", value: questionHtml.slice(lastIndex) });
  }

  return segments;
}

/**
 * Check fill_blanks answers. Each user answer is checked against the
 * corresponding expected answer using existing matchSingle tolerance
 * (numeric equivalence, unit stripping).
 * Returns per-blank results and overall CORRECT/PARTIAL/INCORRECT.
 */
export function checkFillBlanksAdvanced(
  userAnswers: string[],
  expectedAnswers: string[],
  rules?: AnswerRules | null
): { results: CheckResult[]; overall: MultiCheckResult } {
  const results: CheckResult[] = expectedAnswers.map((expected, i) => {
    const userAnswer = userAnswers[i] || "";
    const userNorm = prepare(userAnswer, rules);
    const expectedNorm = prepare(expected, rules);

    if (!userNorm) return "INCORRECT" as CheckResult;
    return matchSingle(userNorm, expectedNorm, rules) ? "CORRECT" as CheckResult : "INCORRECT" as CheckResult;
  });

  const correctCount = results.filter((r) => r === "CORRECT").length;

  let overall: MultiCheckResult;
  if (correctCount === results.length) {
    overall = "CORRECT";
  } else if (correctCount === 0) {
    overall = "INCORRECT";
  } else {
    overall = "PARTIAL";
  }

  return { results, overall };
}
